/**
 * Digital Contact Card for an offline sponsor.
 *
 * Reached from the organizer's emailed link (/sponsor-contact-card/{token}).
 * The sponsor already paid the organizer offline; here they write an offer
 * or a contact card, see how golfers will see it, and pay for the card
 * (Golf Fore Good's product, not part of the sponsorship). On Stripe
 * success we confirm with the content and the card goes live.
 *
 * `embedded`: the same form inside the sponsor assets page (one link for
 * logo, website and the Swag Bag offer). Embedded, it drops the page header
 * and renders nothing once the card is already paid for.
 */
import { useEffect, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';

import {
  checkoutSponsorContactCard,
  confirmSponsorContactCard,
  getSponsorContactCard,
  type SponsorContactCardCheckout,
  type SponsorContactCardContent,
  type SponsorContactCardData,
} from '@/api/embed';
import { PaymentForm } from '@/components/PaymentForm';
import { themedStripeAppearance } from '@/styles/stripeAppearance';
import './PaymentPage.css';
import './SponsorFulfillmentPage.css';
import './SponsorContactCardPage.css';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '';

type Kind = 'offer' | 'contact';

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export function SponsorContactCardPage({ token, embedded = false }: { token: string; embedded?: boolean }) {
  const [data, setData] = useState<SponsorContactCardData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [kind, setKind] = useState<Kind>('offer');
  const [headline, setHeadline] = useState('');
  const [code, setCode] = useState('');
  const [expiry, setExpiry] = useState('');
  const [memo, setMemo] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [checkout, setCheckout] = useState<SponsorContactCardCheckout | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const stripe = useMemo<Promise<Stripe | null> | null>(
    () => (STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null),
    [],
  );

  useEffect(() => {
    let alive = true;
    getSponsorContactCard(token)
      .then((d) => alive && setData(d))
      .catch((err) => {
        if (!alive) return;
        setLoadError(
          err?.response?.status === 404
            ? "This link isn't valid anymore. Reply to the organizer's email and they can send a new one."
            : 'Could not load your card. Please try again in a moment.',
        );
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const content = (): SponsorContactCardContent | null => {
    if (kind === 'offer') {
      return headline.trim()
        ? { coupon: { headline: headline.trim(), code: code.trim() || undefined, expiry: expiry || undefined } }
        : null;
    }
    const card = { memo: memo.trim() || undefined, phone: phone.trim() || undefined, email: email.trim() || undefined };
    return card.memo || card.phone || card.email ? { contact_card: card } : null;
  };

  const onContinue = async () => {
    setError(null);
    if (!content()) {
      setError(kind === 'offer' ? 'Write your offer.' : 'Add a note, phone or email.');
      return;
    }
    setBusy(true);
    try {
      setCheckout(await checkoutSponsorContactCard(token));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not start checkout. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!checkout) return;
    const c = content();
    if (!c) return;
    try {
      await confirmSponsorContactCard(token, checkout.stripe_payment_intent_id, c);
      setDone(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          'Your payment went through but we could not save your card. Reply to the organizer and we will fix it.',
      );
    }
  };

  if (loadError) {
    if (embedded) return null;
    return (
      <div className="tn-fulfill">
        <h1 className="tn-fulfill__title">Digital Swag Bag</h1>
        <div role="alert" className="tn-fulfill__error">{loadError}</div>
      </div>
    );
  }
  if (!data) {
    if (embedded) return null;
    return (
      <div className="tn-fulfill">
        <p className="tn-fulfill__loading">Loading…</p>
      </div>
    );
  }
  if (embedded && (done || data.already_paid)) {
    return done ? (
      <p className="tn-cc__fine">Your card is in the Swag Bag. We emailed you a receipt.</p>
    ) : null;
  }
  if (done || data.already_paid) {
    return (
      <div className="tn-fulfill">
        <h1 className="tn-fulfill__title">Your card is live</h1>
        <p className="tn-fulfill__subtitle">
          Golfers at {data.event_name} can now see your card. {done ? 'We emailed you a receipt.' : ''}
        </p>
      </div>
    );
  }

  const placement = data.hole_number ? `Hole ${data.hole_number} Sponsor` : data.tier_name;

  const breakdown = (
    <div className="tn-payment__breakdown">
      <div className="tn-payment__line">
        <span>Digital Swag Bag</span>
        <span>{money(data.price_cents)}</span>
      </div>
      {data.processing_fee_cents > 0 ? (
        <div className="tn-payment__line">
          <span>Processing fee</span>
          <span>{money(data.processing_fee_cents)}</span>
        </div>
      ) : null}
      <div className="tn-payment__line tn-payment__line--total">
        <span>Total</span>
        <span>{money(data.total_cents)}</span>
      </div>
    </div>
  );

  if (checkout) {
    if (!STRIPE_PUBLISHABLE_KEY) {
      return (
        <div className="tn-card tn-payment__dev-skip">
          <h2>Payment (dev mode)</h2>
          <p>Stripe publishable key not set. Skipping payment for testing.</p>
          <button className="tn-btn tn-btn--primary" onClick={() => void runConfirm()}>
            Mark as paid (dev)
          </button>
          {error ? <div role="alert" className="tn-payment__error">{error}</div> : null}
        </div>
      );
    }
    return (
      <div className="tn-payment">
        <h1 className="tn-payment__title">Payment</h1>
        <p className="tn-payment__subtitle">Digital Swag Bag · {data.event_name}</p>
        {breakdown}
        {stripe ? (
          <Elements stripe={stripe} options={{ clientSecret: checkout.stripe_client_secret, appearance: themedStripeAppearance() }}>
            <PaymentForm submitLabel={`Pay ${money(data.total_cents)}`} onSuccess={runConfirm} />
          </Elements>
        ) : null}
        {error ? <div role="alert" className="tn-payment__error">{error}</div> : null}
      </div>
    );
  }

  return (
    <div className={embedded ? 'tn-cc-embed' : 'tn-fulfill'}>
      {embedded ? null : (
        <>
          <p className="tn-cc__eyebrow">
            {placement} · {data.event_name}
          </p>
          <h1 className="tn-fulfill__title">Add your offer to the Digital Swag Bag</h1>
          <p className="tn-fulfill__subtitle">
            Every golfer at {data.event_name} gets a Swag Bag in the Golf Fore Good app. Your offer or contact card
            goes in it, on their phones during the round and still there after the event.
          </p>
        </>
      )}

      <div className="tn-cc__segment" role="tablist">
        {(['offer', 'contact'] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={kind === k}
            className={`tn-cc__seg${kind === k ? ' tn-cc__seg--on' : ''}`}
            onClick={() => setKind(k)}
          >
            {k === 'offer' ? 'Offer' : 'Contact Card'}
          </button>
        ))}
      </div>

      <div className="tn-fulfill__form">
        {kind === 'offer' ? (
          <>
            <label className="tn-field">
              <span className="tn-field__label">Your Offer *</span>
              <input
                className="tn-field__input"
                value={headline}
                maxLength={120}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="Show this card for 10% off your next service"
              />
            </label>
            <label className="tn-field">
              <span className="tn-field__label">Promo Code</span>
              <input
                className="tn-field__input"
                value={code}
                maxLength={40}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Optional"
              />
            </label>
            <label className="tn-field">
              <span className="tn-field__label">Good Through</span>
              <input
                className="tn-field__input"
                type="date"
                value={expiry}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setExpiry(e.target.value)}
              />
              <span className="tn-field__hint">
                Optional. After this date the offer shows as ended in golfers’ Swag Bags and Wallets.
              </span>
            </label>
          </>
        ) : (
          <>
            <label className="tn-field">
              <span className="tn-field__label">Phone</span>
              <input className="tn-field__input" type="tel" value={phone} maxLength={40} onChange={(e) => setPhone(e.target.value)} />
            </label>
            <label className="tn-field">
              <span className="tn-field__label">Email</span>
              <input className="tn-field__input" type="email" value={email} maxLength={120} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="tn-field">
              <span className="tn-field__label">Short Note</span>
              <input
                className="tn-field__input"
                value={memo}
                maxLength={120}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="Ask for Jane in sales"
              />
            </label>
          </>
        )}

        {breakdown}

        {error ? <div role="alert" className="tn-fulfill__error">{error}</div> : null}
        <button type="button" className="tn-btn tn-btn--primary" onClick={onContinue} disabled={busy}>
          {busy ? 'Working…' : `Continue to payment · ${money(data.total_cents)}`}
        </button>
        <p className="tn-cc__fine">
          Paid to Golf Fore Good for the digital card. This is a service fee, not a charitable contribution, and
          isn't part of your sponsorship. Your card goes live as soon as payment clears.
        </p>
      </div>
    </div>
  );
}
